import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFeedback } from '@/hooks/useFeedback';
import { cn } from '@/lib/utils';

const FEEDBACK_TYPE_COLORS = {
  bug: 'destructive',
  feature: 'default',
  help: 'secondary',
  other: 'outline',
  todo: 'default',
} as const;

const FEEDBACK_TYPE_LABELS = {
  bug: 'Bug Report',
  feature: 'Feature Request',
  help: 'Help Request',
  other: 'Other',
  todo: 'Todo Feedback',
} as const;

export default function FeedbackTable() {
  const navigate = useNavigate({ from: '/feedback' });
  const search = useSearch({ from: '/feedback' });
  const page = search.page;
  const limit = 50;

  const { data, isLoading, error, refetch, isRefetching } = useFeedback(
    page,
    limit
  );

  const totalPages = data?.total ? Math.ceil(data.total / limit) : 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">
            Error Loading Feedback
          </CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>User Feedback</CardTitle>
              <CardDescription>
                View and manage user feedback submissions
                {data?.total && ` (${data.total} total)`}
              </CardDescription>
            </div>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              disabled={isRefetching}
            >
              <RefreshCw
                className={cn('h-4 w-4 mr-2', isRefetching && 'animate-spin')}
              />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Feedback</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.feedbacks.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No feedback found
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.feedbacks.map((feedback) => (
                        <TableRow key={feedback.id}>
                          <TableCell>
                            <Badge
                              variant={
                                FEEDBACK_TYPE_COLORS[feedback.feedback_type]
                              }
                            >
                              {FEEDBACK_TYPE_LABELS[feedback.feedback_type]}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md">
                            <div className="space-y-1">
                              <p className="text-sm">
                                {truncateText(feedback.text)}
                              </p>
                              {feedback.text.length > 100 && (
                                <details className="text-xs text-muted-foreground">
                                  <summary className="cursor-pointer hover:text-foreground">
                                    Show full text
                                  </summary>
                                  <p className="mt-2 whitespace-pre-wrap">
                                    {feedback.text}
                                  </p>
                                </details>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm font-medium">
                                {feedback.user_email}
                              </p>
                              {feedback.user_name && (
                                <p className="text-xs text-muted-foreground">
                                  {feedback.user_name}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(feedback.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate({
                          search: { ...search, page: Math.max(1, page - 1) },
                        })
                      }
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate({
                          search: {
                            ...search,
                            page: Math.min(totalPages, page + 1),
                          },
                        })
                      }
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
